import React, { useMemo } from 'react';
import { Card, Descriptions, Tag, Space, Typography } from 'antd';
import { UserOutlined, IdcardOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { getActualLoanData, getEmployeeName, resolveEssPayload } from './essPayload';

const { Text } = Typography;

interface ESSEmployeeInfoViewProps {
    data: any;
    formikProps?: any;
}

const ESSEmployeeInfoView: React.FC<ESSEmployeeInfoViewProps> = ({ data, formikProps }) => {
    const actualData = getActualLoanData(data, formikProps);

    // Parse ESS request payload
    const essPayload = useMemo(() => {
        return resolveEssPayload(actualData);
    }, [actualData?.essRequestPayload, actualData?.formattedEssRequestPayload]);

    if (!essPayload) {
        return null;
    }

    const employeeName = getEmployeeName(essPayload);
    const isPermanent = String(essPayload.termsOfEmployment || '').toUpperCase().includes('PERMANENT');
    const employeePhone = essPayload.phoneNumber || essPayload.mobileNumber || essPayload.telephoneNumber;
    const employeeEmail = essPayload.email || essPayload.emailAddress;
    const yearsToRetirement = essPayload.retirementDate || essPayload.retirementYears;

    return (
        <Card 
            title={
                <Space>
                    <UserOutlined />
                    <span>Employee Information from ESS</span>
                </Space>
            }
            className="mb-4"
        >
            <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }}>
                <Descriptions.Item label="Full Name" span={2}>
                    <strong>{employeeName || 'N/A'}</strong>
                </Descriptions.Item>

                <Descriptions.Item label="Sex">
                    <Tag color={essPayload.sex === 'M' ? 'blue' : 'pink'}>
                        {essPayload.sex === 'M' ? 'Male' : essPayload.sex === 'F' ? 'Female' : 'N/A'}
                    </Tag>
                </Descriptions.Item>

                <Descriptions.Item label="National ID (NIN)" span={2}>
                    <Text copyable>{essPayload.nin || 'N/A'}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Marital Status">
                    {essPayload.maritalStatus || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label={<><IdcardOutlined /> Check Number</>}>
                    <strong>{essPayload.checkNumber || 'N/A'}</strong>
                </Descriptions.Item>

                <Descriptions.Item label="Designation Code">
                    {essPayload.designationCode || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Designation Name" span={2}>
                    {essPayload.designationName || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Job Class Code">
                    {essPayload.jobClassCode || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Terms of Employment" span={2}>
                    <Tag color={isPermanent ? 'green' : 'orange'}>
                        {essPayload.termsOfEmployment || 'N/A'}
                    </Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Employment Date">
                    {essPayload.employmentDate || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Confirmation Date">
                    {essPayload.confirmationDate || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Years to Retirement">
                    <Tag color="volcano">{yearsToRetirement || 'N/A'} years</Tag>
                </Descriptions.Item>

                <Descriptions.Item label={<><PhoneOutlined /> Phone Number</>}>
                    <Text copyable>{employeePhone || 'N/A'}</Text>
                </Descriptions.Item>

                <Descriptions.Item label={<><MailOutlined /> Email</>} span={2}>
                    <Text copyable>{employeeEmail || 'N/A'}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Physical Address" span={3}>
                    {essPayload.physicalAddress || 'N/A'}
                </Descriptions.Item>
            </Descriptions>
        </Card>
    );
};

export default ESSEmployeeInfoView;
